import { useEffect } from "react";
import API from "../../utils/API";

const PUBLIC_VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isStandalone = () =>
    window.navigator.standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches;

export const usePushNotifications = () => {
    useEffect(() => {
        if (!window.isSecureContext) return;
        if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
        if (isIOS() && !isStandalone()) return;

        const subscribe = async (registration) => {
            try {
                const existing = await registration.pushManager.getSubscription();
                if (existing) {
                    await API.post("/api/staff-notification/push-subscription", existing.toJSON());
                    return;
                }
                const subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY),
                });
                await API.post("/api/staff-notification/push-subscription", subscription.toJSON());
            } catch (err) {
                console.error("[PushNotifications] Subscribe error:", err);
            }
        };

        const register = async () => {
            try {
                const registration = await navigator.serviceWorker.ready;

                if (isIOS()) {
                    if (Notification.permission === "granted") {
                        // Already granted from a previous session — subscribe immediately
                        await subscribe(registration);
                    } else if (Notification.permission === "default") {
                        // Permission prompt hasn't been answered yet.
                        // Poll for the result since iOS doesn't support
                        // the permissionchange event reliably on PWAs
                        const interval = setInterval(async () => {
                            if (Notification.permission === "granted") {
                                clearInterval(interval);
                                await subscribe(registration);
                            } else if (Notification.permission === "denied") {
                                clearInterval(interval);
                            }
                        }, 1000);
                        // Stop polling after 60s regardless
                        setTimeout(() => clearInterval(interval), 60000);
                    }
                } else {
                    const permission = await Notification.requestPermission();
                    if (permission !== "granted") return;
                    await subscribe(registration);
                }
            } catch (err) {
                console.error("[PushNotifications] Registration error:", err);
            }
        };

        register();
    }, []);
};

// Call this inside a button onClick — iOS Safari requires a user gesture for
// the first Notification.requestPermission() call.
export const requestPushPermission = async () => {
    if (!PUBLIC_VAPID_KEY) {
        console.error("Missing REACT_APP_VAPID_PUBLIC_KEY");
        return false;
    }
    if (!window.isSecureContext) return false;
    if (!("serviceWorker" in navigator)) return false;
    if (!("PushManager" in window)) return false;
    if (!("Notification" in window)) return false;
    if (!PUBLIC_VAPID_KEY) return false;
    if (isIOS() && !isStandalone()) return false;
    if (Notification.permission === "denied") return false;
    try {
        const registration = await navigator.serviceWorker.ready;
        const permission =
            Notification.permission === "granted"
                ? "granted"
                : await Notification.requestPermission();

        if (permission !== "granted") return false;
        const existing = await registration.pushManager.getSubscription();
        const subscription =
            existing ||
            await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY),
            });

        await API.post(
            "/api/staff-notification/push-subscription",
            subscription.toJSON()
        );
        return true;
    } catch (err) {
        console.error("[PushNotifications] Permission request error:", err);
        return false;
    }
};

export const reregisterPushSubscription = async () => {
    try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration) return;

        const existing = await registration.pushManager.getSubscription();
        if (!existing) return;

        // Re-save the existing subscription — backend will update with new business context
        await API.post("/api/staff-notification/push-subscription", existing.toJSON());
    } catch (err) {
        console.error("[PushNotifications] Re-register error:", err);
    }
};


export const unregisterPushSubscription = async () => {
    try {
        const registration = await navigator.serviceWorker.getRegistration();
        const subscription = await registration?.pushManager.getSubscription();
        if (!subscription) return;

        await API.delete("/api/staff-notification/push-subscription", {
            data: { endpoint: subscription.endpoint }
        });

        await subscription.unsubscribe();
    } catch (err) {
        console.error("[PushNotifications] Unsubscribe error:", err);
    }
};

